import { TbCoins, TbRefresh, TbEye } from 'react-icons/tb'
import { useTranslation } from 'react-i18next'
import { NavbarProps } from './Navbar.types'
import { UserAvatar } from '../UserAvatar'
import { LanguageDropdown } from '../LanguageDropdown'
import ThemeToggle from '../ThemeToggle/ThemeToggle'
import { LANGUAGES } from '../../i18n/constants'
import { USER_BALANCE_UNDEFINED } from '../../constants/string'

const defaultLanguages = LANGUAGES

const Navbar = ({
  logo,
  username,
  balance,
  onLogoClick,
  onAvatarClick,
  currentLang,
  onLanguageChange,
  languages = defaultLanguages,
  onRefreshClick,
  isUpdating = false,
  cooldown = 0,
  isDisabled = false,
  isMaskedBalance = false,
}: NavbarProps) => {
  const { t } = useTranslation()

  const handleRefreshClick = () => {
    if (isDisabled || !onRefreshClick) return
    onRefreshClick()
  }

  // Determine mask state and which icon to render
  const shouldMask = isMaskedBalance && balance === undefined
  const ActionIcon = shouldMask ? TbEye : TbRefresh

  return (
    <nav className="bg-[var(--navbar-bg)] shadow-sm sticky top-0 z-40">
      <div className="flex items-center justify-between w-full max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-12 xl:px-20 2xl:px-0 py-4">
        {/* Logo */}
        {logo && (
          <img
            src={logo}
            alt={t('navbar.logoAlt')}
            className="h-8 sm:h-10 w-20 sm:w-24 object-contain cursor-pointer"
            onClick={onLogoClick}
          />
        )}

        {/* Right Section */}
        <div className="flex items-center gap-3 sm:gap-2">
          {/* Desktop Username - Hidden on mobile */}
          <span className="hidden sm:block text-xs sm:text-sm font-semibold text-[var(--user-id-text)]">
            {username}
          </span>

          {/* Balance Button */}
          <button
            className={`flex items-center h-8 sm:h-10 active-gradient text-[var(--balance-text-color)] px-3 py-2 rounded-sm text-sm font-semibold gap-2 ${
              isUpdating ? 'animate-pulse' : ''
            }`}
          >
            <TbCoins
              className={`text-lg sm:text-2xl mr-1 sm:mr-2 transition-all duration-300 ${
                isUpdating ? 'animate-bounce' : ''
              }`}
            />
            <span className="font-bold whitespace-nowrap">
              {shouldMask ? USER_BALANCE_UNDEFINED : balance}
            </span>
            <div className="relative ml-1 sm:ml-2">
              <ActionIcon
                className={`text-lg sm:text-xl cursor-pointer transition-all duration-300 ${
                  !shouldMask && isUpdating ? 'animate-spin' : ''
                } ${
                  isDisabled
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:opacity-80'
                }`}
                onClick={handleRefreshClick}
              />
              {cooldown > 0 && (
                <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {cooldown}
                </div>
              )}
            </div>
          </button>

          {/* Desktop Language & Theme - Hidden on mobile */}
          <div className="hidden sm:flex items-center gap-2">
            <LanguageDropdown
              currentLang={currentLang}
              onLanguageChange={onLanguageChange}
              languages={languages}
            />
            <ThemeToggle />
          </div>

          {/* Mobile User Avatar - Hidden on desktop */}
          <button className="sm:hidden">
            <UserAvatar
              username={username}
              size="navbar"
              onClick={onAvatarClick}
            />
          </button>
        </div>
      </div>
    </nav>
  )
}

export default Navbar
